import React from 'react';
import PropTypes from 'prop-types';

import noop from 'lodash/noop';
import get from 'lodash/get';
import without from 'lodash/without';

import { MatchPropTypes } from '@models/Match';
import BracketTeam from '@views/BracketTeam';

const getSideProps = (
  side,
  { hasBothTeams, canEditScore, canEditTeams, teams, ...props }
) => {
  const team = get(props, `team${side}`);
  return {
    side: side.toLowerCase(),
    winnerPosition: 'edge',
    team,
    score: hasBothTeams ? get(props, `score${side}`) : noop(),
    from: get(props, `from${side}`),
    canEditScore,
    canEditTeam: canEditTeams,
    showCheckbox: hasBothTeams
      ? get(props, `scoreHome`, false) === get(props, `scoreAway`, true)
      : false,
    teams: get(teams, side.toLowerCase()),
    teamGroup: team
      ? get(props, ['teamGroupByCode', String(team).toLowerCase()])
      : undefined,
    isWinner: get(props, 'winner') === side.toLowerCase(),
    onChangeScore: (value) => {
      const otherSide = side === 'Home' ? 'Away' : 'Home';
      let winnerChange = {};
      if (hasBothTeams && get(props, `score${otherSide}`) !== value) {
        winnerChange = { winner: undefined };
      }
      props.onChange({ [`score${side}`]: value, ...winnerChange });
    },
    onChangeTeam: (value) => props.onChange({ [`team${side}`]: value }),
    onChangeWinner: (e) =>
      props.onChange({ winner: e.target.checked ? side.toLowerCase() : '' }),
  };
};

// Mapeo de assertions v2 a clases CSS v1
const mapAssertionsToCssClasses = (assertions = []) => {
  return assertions.map(assertion => {
    // Sistema v2 -> Sistema v1 (CSS)
    if (assertion === 'resultado-correcto') return 'groups-winner';
    if (assertion === 'resultado-exacto') return 'groups-winner groups-score';
    if (assertion === 'equipo-clasificado') return 'bracket-winner';
    if (assertion === 'resultado-exacto-eliminatoria') return 'bracket-winner bracket-score';
    // Sistema v1 (mantener como está)
    return assertion;
  }).join(' ');
};

// Determina el ganador basándose en scores o el campo winner
const getMatchWinner = (match) => {
  if (!match) return '';
  
  // Si hay winner explícito (penales), usarlo
  if (match.winner === 'home' || match.winner === 'away') {
    return match.winner;
  }
  
  // Si hay scores, calcular ganador
  const scoreHome = match.scoreHome;
  const scoreAway = match.scoreAway;
  
  if (scoreHome != null && scoreAway != null && scoreHome !== scoreAway) {
    return scoreHome > scoreAway ? 'home' : 'away';
  }
  
  return '';
};

export const getMatchClass = (match) => {
  const winner = getMatchWinner(match);
  return `${match.isToday ? 'match-today' : ''} ${mapAssertionsToCssClasses(without(
    get(match, 'assertions', []),
    'champion'
  ))} ${winner ? `winner-${winner}` : ''}`.trim();
};

const BracketMatch = ({ round, ...props }) => (
  <div className={`match-mobile ${round} ${getMatchClass(props)}`}>
    <div className="team team--home">
      <BracketTeam {...getSideProps('Home', props)} />
    </div>
    <div className="team team--away">
      <BracketTeam {...getSideProps('Away', props)} />
    </div>
  </div>
);

BracketMatch.displayName = 'BracketMatch';
BracketMatch.propTypes = Object.assign({}, MatchPropTypes, {
  canEditScore: PropTypes.bool,
  canEditTeams: PropTypes.bool,
  hasBothTeams: PropTypes.bool,
  teams: PropTypes.shape({
    home: PropTypes.arrayOf(PropTypes.string),
    away: PropTypes.arrayOf(PropTypes.string),
  }),
  teamGroupByCode: PropTypes.object,
  onChange: PropTypes.func,
});
BracketMatch.defaultProps = {
  hasBothTeams: false,
  canEditScore: false,
  canEditTeams: false,
  teamGroupByCode: {},
  onChange: noop,
};

export default BracketMatch;
